<template>
    <div>
        <div class="row q-col-gutter-sm">
            <div class="col-12 col-md-6">
                <q-input
                    v-model.number="lat"
                    type="number"
                    step="0.000001"
                    :label="
                        latitudeLabel ||
                        (window.translations
                            ? window.translations['admin.map.latitude']
                            : 'Latitude')
                    "
                    :name="latitudeName"
                    outlined
                    dense
                    @update:model-value="updateMarkerFromInputs"
                />
            </div>
            <div class="col-12 col-md-6">
                <q-input
                    v-model.number="lng"
                    type="number"
                    step="0.000001"
                    :label="
                        longitudeLabel ||
                        (window.translations
                            ? window.translations['admin.map.longitude']
                            : 'Longitude')
                    "
                    :name="longitudeName"
                    outlined
                    dense
                    @update:model-value="updateMarkerFromInputs"
                />
            </div>
        </div>

        <q-btn
            icon="map"
            :label="
                buttonLabel ||
                (window.translations
                    ? window.translations['admin.map.pick_on_map']
                    : 'Pick on map')
            "
            @click="showDialog = true"
            color="primary"
            class="q-mt-sm full-width"
        />

        <q-dialog
            v-model="showDialog"
            maximized
            transition-show="slide-up"
            transition-hide="slide-down"
        >
            <q-card>
                <q-toolbar class="bg-primary text-white">
                    <q-btn flat round dense icon="close" v-close-popup />
                    <q-toolbar-title>{{
                        dialogTitle ||
                        (window.translations
                            ? window.translations["admin.map.pick_point_title"]
                            : "Pick point on map")
                    }}</q-toolbar-title>
                </q-toolbar>

                <q-card-section
                    class="q-pa-none"
                    style="height: calc(100vh - 50px)"
                >
                    <l-map
                        ref="map"
                        style="height: 100%; width: 100%"
                        :zoom="zoom"
                        :max-zoom="maxZoom"
                        :center="center"
                        @click="onMapClick"
                        @ready="onMapReady"
                    >
                        <l-tile-layer
                            :url="tileUrl"
                            :attribution="attribution"
                            :options="{
                                className: monochrome ? 'monochrome-tiles' : '',
                                maxZoom: 20,
                                maxNativeZoom: 18,
                            }"
                        ></l-tile-layer>
                        <l-marker
                            v-if="marker"
                            :lat-lng="marker"
                            draggable
                            @dragend="onMarkerDragEnd"
                        ></l-marker>
                    </l-map>
                </q-card-section>

                <q-btn
                    fab
                    icon="check"
                    color="secondary"
                    @click="confirmSelection"
                    :label="
                        window.translations
                            ? window.translations['admin.map.confirm']
                            : 'Confirm'
                    "
                    class="absolute-bottom-right q-ma-lg"
                    style="z-index: 3000"
                />
            </q-card>
        </q-dialog>
    </div>
</template>

<style scoped>
:deep(.monochrome-tiles) {
    filter: grayscale(100%);
}
</style>

<script setup>
import { ref, onMounted, watch, nextTick } from "vue";
import { LMap, LTileLayer, LMarker } from "@vue-leaflet/vue-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix missing icon issues in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import iconUrl from "leaflet/dist/images/marker-icon.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";

L.Icon.Default.mergeOptions({
    iconRetinaUrl: iconRetinaUrl,
    iconUrl: iconUrl,
    shadowUrl: shadowUrl,
});

const props = defineProps({
    initialLat: {
        type: [Number, String],
        default: null,
    },
    initialLng: {
        type: [Number, String],
        default: null,
    },
    latitudeName: {
        type: String,
        default: "latitude",
    },
    longitudeName: {
        type: String,
        default: "longitude",
    },
    latitudeLabel: {
        type: String,
        default: "",
    },
    longitudeLabel: {
        type: String,
        default: "",
    },
    buttonLabel: {
        type: String,
        default: "",
    },
    dialogTitle: {
        type: String,
        default: "",
    },
    centerLat: {
        type: [Number, String],
        default: 51.165691,
    },
    centerLng: {
        type: [Number, String],
        default: 10.451526,
    },
    monochrome: {
        type: Boolean,
        default: false,
    },
});

const lat = ref(props.initialLat);
const lng = ref(props.initialLng);
const showDialog = ref(false);
const zoom = ref(15);
const maxZoom = ref(20);
const center = ref([parseFloat(props.centerLat), parseFloat(props.centerLng)]);
const tileUrl = ref("/tiles/{z}/{x}/{y}.png");
const attribution = ref(
    '&copy; <a target="_blank" rel="noopener" href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
);
const marker = ref(null);
const map = ref(null);

onMounted(() => {
    if (props.initialLat && props.initialLng) {
        const l = parseFloat(props.initialLat);
        const n = parseFloat(props.initialLng);
        if (!isNaN(l) && !isNaN(n)) {
            marker.value = { lat: l, lng: n };
            center.value = [l, n];
            lat.value = l;
            lng.value = n;
        }
    }
});

const onMapReady = () => {
    nextTick(() => {
        if (marker.value) {
            center.value = [marker.value.lat, marker.value.lng];
        }
    });
};

const onMapClick = (event) => {
    marker.value = event.latlng;
};

const onMarkerDragEnd = (event) => {
    marker.value = event.target.getLatLng();
};

const updateMarkerFromInputs = () => {
    if (lat.value && lng.value) {
        marker.value = {
            lat: parseFloat(lat.value),
            lng: parseFloat(lng.value),
        };
        center.value = [parseFloat(lat.value), parseFloat(lng.value)];
    }
};

const confirmSelection = () => {
    if (marker.value) {
        lat.value = marker.value.lat.toFixed(6);
        lng.value = marker.value.lng.toFixed(6);
    }
    showDialog.value = false;
};

watch(showDialog, (val) => {
    if (val) {
        nextTick(() => {
            if (marker.value) {
                center.value = [marker.value.lat, marker.value.lng];
            } else {
                center.value = [
                    parseFloat(props.centerLat),
                    parseFloat(props.centerLng),
                ];
            }
        });
    }
});
</script>
